import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from "react-router-dom"
import { Grid, Box, Chip, CircularProgress, Link, TableFooter, TablePagination, TableRow, TextField, Typography, alpha } from "@mui/material"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import { useToast } from "@hooks"
import PageWrapper from "Pages/@PageWrapper"
import { dsColorNew } from "@palette"
import {
	AppCardTitle, AppDataTable, AppToolbarCreateButton, AppAntSwitch, AppButtonIcon, AppResultsSkeleton, AppValueChip,
	AppStatusFilterToggle, HIDDEN_FILTER_CHIP, AppNote, secondaryButtonSx,
} from "@components"
import { toneChipSx } from "Pages/DsRepricing/@components/tones"
import { DS_REPRICING_ROUTES } from "@constants/routes"
import {
	dsRepricingDrawerData,
	dsRepricingListData,
	dsRepricingStatusFilter,
	dsRepricingIsLoading,
	dsRepricingIsSubmitting,
	dsRepricingOriginalData,
} from "Pages/DsRepricing/Page/@signals"
import DsRepricingDrawer from "Pages/DsRepricing/Page/Drawer"
import { weekdaysLabel } from "Pages/DsRepricing/Page/@components/Weekdays"
import { emptyPeriod, readPeriod } from "Pages/DsRepricing/Page/@components/periodShape"
import useDsRepricingTable from "Pages/DsRepricing/Page/@hooks/useTable"
import useDsRepricingFormSubmit from "Pages/DsRepricing/Page/@hooks/useFormSubmit"
import useDsRepricingActions from "Pages/DsRepricing/Page/@hooks/useActions"
import useDsRepricingStatus from "Pages/DsRepricing/Page/@hooks/useStatus"
import useDsRepricingEdit from "Pages/DsRepricing/Page/@hooks/useEdit"
import useDsRepricingClone from "Pages/DsRepricing/Page/@hooks/useClone"
import ConfirmEditModal from "Pages/DsRepricing/Page/@components/ConfirmEditModal"
import OverlapModal from "Pages/DsRepricing/Page/@components/OverlapModal"
import { DEFAULT_PRIORITY } from "Pages/DsRepricing/Page/@components/Priority"
import { WHEN_IT_RUNS_CARD, WHEN_IT_RUNS_STEP } from "Pages/DsRepricing/Page/Drawer/steps"
import { AxisCatalogProvider } from "Pages/DsRepricing/Setup/@services/axes"
import { SiteCatalogProvider, useSiteCatalog } from "Pages/DsRepricing/Setup/@services/sites"
import { PricelistCatalogProvider } from "Pages/DsRepricing/Setup/@services/pricelists"
import { ProductsProvider, useProductsCatalog } from "Pages/DsRepricing/Setup/@services/useProducts"
import useConfirm from "Pages/DsRepricing/@components/useConfirm"
import MigrationBanner from "Pages/DsRepricing/@components/MigrationBanner"
import { alignConfirmOptions } from "Pages/DsRepricing/Page/@components/AlignConfirm"
import { alignHours, cadenceSummary, factoryCadence, fmtInt } from "Pages/DsRepricing/Page/@services/cadence"
import { DEFAULT_COMPARE, fromServer, toServer } from "Pages/DsRepricing/Page/@services/adapters"
import { strategiesApi } from "Pages/DsRepricing/Page/@services/api"
import {
	BODY, NO_PREVIEW_DETAIL, PRICE_POINT_DEFINITION, PRICE_POINT_SHARED, SUBTITLE, confirmLabel, launchOutcome, launchesNothing,
	notLaunched, notLaunchedLabel, pricePointsLabel, productRunNowPreview, runNowState, searchesLabel, strategiesLabel, titleOf,
} from "Pages/DsRepricing/Page/@services/runNow"
import { LOCKED_OPEN, flatten, footerLabel, groupByProduct, productMeta, strategyCountLabel, toggleExpanded } from "Pages/DsRepricing/Page/@services/byProduct"
import { RateShopsProvider, perStrategy, useTenantRateShops } from "Pages/DsRepricing/Page/@hooks/useRateShops"
import { transformData } from "Pages/DsRepricing/Page/@hooks/useFormSubmit"
import { productSites, usedBy } from "Pages/DsRepricing/Setup/@services/products"
import { alignSiteScope } from "Pages/DsRepricing/Page/@services/siteScope"

/**
 * Three states, one switch. The model has `draft | enabled | disabled`
 * (PRD 04) and the switch can only say two things, so the third is shown
 * next to it: a draft has never been live, a disabled one was. Painting
 * them the same would leave the operator unable to tell "I never turned
 * this on" from "I turned it off" (brief criterio 26).
 */
function StatusSwitch({ value, row }) {
	const { handleStatus } = useDsRepricingStatus(row.id, row.name)
	const isDraft = row.status === 'draft'
	// A strategy with no valid product cannot be switched on (UX D3): the
	// PMS would refuse every write. Off is always allowed.
	const tariffOk = row.product?.state === 'ok'
	const blocked = !value && !tariffOk
	const blockedTitle = row.product?.state === 'none'
		? 'Pick a product before switching it on'
		: 'The product is invalid — fix it in Setup → Products or choose another before switching it on'
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.75 }}
		     title={blocked ? blockedTitle : undefined}>
			<AppAntSwitch checked={value} disabled={blocked} onChange={() => !blocked && handleStatus(!value)}/>
			{isDraft && (
				<AppValueChip tone="accent"
				              title="Never activated. Turn it on to let the engine run it."
				              sx={{ py: 0.25, fontSize: 11, fontWeight: 600 }}>
					Draft
				</AppValueChip>
			)}
		</Box>
	)
}

// No Run now on a strategy (CU-869fm5juc, criterion 10): it lives on the product's header.
function ActionsCell({ row, onDelete }) {
	const { onEdit, isLoading: isEditLoading } = useDsRepricingEdit(row.id)
	const { onClone, isLoading: isCloneLoading } = useDsRepricingClone(row.id)
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
			{isEditLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="pen" tooltip="Edit" onClick={e => { e.stopPropagation(); onEdit() }}/>
			}
			{isCloneLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="clone" tooltip="Clone" onClick={e => { e.stopPropagation(); onClone() }}/>
			}
			<AppButtonIcon
				icon="trash-can"
				tooltip="Delete"
				onClick={e => { e.stopPropagation(); onDelete(row.id, row.name) }}
			/>
		</Box>
	)
}

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v}</Box>

const STRATEGY_LABEL = { market_trend: 'Market trend', ranking: 'Ranking' }

const strategyLabel = s => {
	if (!s?.type) return '-'
	const base = STRATEGY_LABEL[s.type] ?? s.type
	if (s.type === 'ranking') return `${base} position ${s.targetPosition}`
	const extras = [
		s.rankCap != null ? `cap #${s.rankCap}` : null,
		s.excludeOutliersPct != null ? `>${s.excludeOutliersPct}% out` : null,
	].filter(Boolean)
	return extras.length ? `${base} · ${extras.join(', ')}` : base
}

// The undercut is a sub-line of the strategy, not a column (UX D6):
// "Market trend −2.5 %", "Ranking position 1 · −1 €".
const undercutSign = u => (u?.direction === 1 ? '+' : '−')
const strategyCell = (s, undercut) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
		<span>{strategyLabel(s)}</span>
		<Box component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
			{undercut?.value != null ? `${undercutSign(undercut)}${undercut.value} ${undercut.type === 0 ? '€' : '%'}` : 'no undercut'}
		</Box>
	</Box>
)

const strategyFilterOptions = data => ({
	names: [...new Set(data.map(row => STRATEGY_LABEL[row?.strategy?.type]).filter(Boolean))],
	logic: (value, filters) => {
		if (!filters?.length) return false
		return !filters.includes(STRATEGY_LABEL[value?.type])
	},
})

const tethersSummary = ids => {
	const n = Array.isArray(ids) ? ids.length : 0
	return n === 0 ? '-' : `${n} tether${n === 1 ? '' : 's'}`
}

const undercutLabel = v => v?.value != null ? `${v.value} ${v.type === 0 ? '€' : '%'}` : null

const undercutFilterOptions = data => ({
	names: [...new Set(data.map(row => undercutLabel(row?.undercut)).filter(Boolean))],
	logic: (value, filters) => {
		if (!filters?.length) return false
		return !filters.includes(undercutLabel(value))
	},
})

const rangeLabel = (r, fromKey, toKey) => `${r[fromKey]} → ${r[toKey]}`

// One column for the three period modes: a rule carries seasons, lead time windows or weekdays, never
// two, so whichever one is filled is the whole answer to "when does this apply". Before this the
// column showed only seasons, which left a lead time or weekday rule looking like it had no period.
const periodLabels = period => {
	const { seasons, leadTimes, weekdays } = readPeriod({ period })
	if (seasons.length > 0) return seasons.map(s => `${s.seasonFrom} → ${s.seasonTo}`)
	if (leadTimes.length > 0) return leadTimes.map(w => `Lead ${w.leadFrom} → ${w.leadTo} d`)
	if (weekdays.length > 0) return [weekdaysLabel(weekdays)]
	return []
}

const rangeFilterOptions = (data, field, fromKey, toKey) => ({
	names: [...new Set(data.flatMap(row => (row?.[field] || []).map(r => rangeLabel(r, fromKey, toKey))))],
	logic: (arr, filters) => {
		if (!filters?.length) return false
		if (!Array.isArray(arr) || arr.length === 0) return true
		return !arr.some(r => filters.includes(rangeLabel(r, fromKey, toKey)))
	},
})

// Filtering by period covers the three modes, because the dropdown is built from the same labels the
// cell shows: a season range, a lead time window or the list of weekdays.
const periodFilterOptions = data => ({
	names: [...new Set(data.flatMap(row => periodLabels(row?.period)))],
	logic: (period, filters) => {
		if (!filters?.length) return false
		const labels = periodLabels(period)
		if (labels.length === 0) return true
		return !labels.some(label => filters.includes(label))
	},
})

/**
 * What the table shows beyond the strategy row (CU-869fhuhy6, UX D6,
 * brief v3), folded into each row once per list so the cells and the
 * column filters read the same strings: the product and its verdict
 * (`row.product`, the server's), the sites named from the catalogue, the
 * cadence summary, and the engine's searches / day for this strategy
 * (`GET /strategies/rate-shops`, UX D5). An empty site scope means every
 * site of the product.
 */
const siteIds = (row) => (Array.isArray(row.filterGroupSites) ? row.filterGroupSites.map(o => Number(o?.value ?? o)).filter(Number.isFinite) : [])
const siteLabels = (row, sites) => siteIds(row).map(id => sites.labelOf(id))

const decorate = (rows, shops, products, sites) => rows.map(row => {
	const product = products.find(p => String(p.id) === String(row.productId)) || null
	const verdict = row.product || { id: row.productId, name: product?.name || null, state: row.productId == null ? 'none' : (product ? 'ok' : 'invalid'), reason: null }
	const others = usedBy(row.productId, rows, row.id)
	const [cadenceLine1, cadenceLine2] = cadenceSummary(row.whenItRuns, row.period)
	const shop = perStrategy(shops, row.id)
	// Criterion 10 (UX v3 D20): a site the strategy keeps but its product no longer has — the engine neither
	// searches nor decides there. The server says which (`sitesStale`); marked amber (information, not an error).
	const stale = new Set((row.sitesStale || []).map(id => sites.labelOf(id)))
	return {
		...row,
		product: { ...verdict, product, others, rows: product?.rows || [] },
		productName: verdict.name || product?.name || 'no product',
		sitesLabels: siteLabels(row, sites),
		sitesStale: [...stale],
		productSiteCount: productSites(product).length,
		cadenceLine1, cadenceLine2,
		searchesPerDay: shop?.searches_per_day ?? null,
		searchesShared: shop?.shared_per_day ?? 0,
		shop,
	}
})

const STALE_SITE_TITLE = 'The strategy neither searches nor decides on it; pick another site or add the row back in Setup → Products'
const sitesCell = (row) => (row.sitesLabels?.length
	? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
		{row.sitesLabels.map((l, i) => (row.sitesStale?.includes(l)
			? <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }} title={STALE_SITE_TITLE}>
				<span>{l}</span>
				<Chip size="small" label="site no longer in the product" sx={toneChipSx('warn')}/>
			</Box>
			: <span key={i}>{l}</span>))}
	</Box>
	: <Box component="span" sx={{ fontStyle: 'italic', color: dsColorNew.textMuted }}
	       title="No site chosen in Scope: the strategy is compared on every site of its product.">
		{row.product?.product ? `All ${row.productSiteCount} site${row.productSiteCount === 1 ? '' : 's'} of the product` : 'All sites'}
	</Box>)

const sitesFilterOptions = data => ({
	names: [...new Set(data.flatMap(row => row?.sitesLabels?.length ? row.sitesLabels : ['All sites']))],
	logic: (labels, filters) => {
		if (!filters?.length) return false
		if (!labels?.length) return !filters.includes('All sites')
		return !labels.some(l => filters.includes(l))
	},
})

const cadenceCell = (row) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
		<OpenStrategyLink id={row.id}><Box component="span" sx={{ fontWeight: 400, color: dsColorNew.textDark }}>{row.cadenceLine1}</Box></OpenStrategyLink>
		<Box component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{row.cadenceLine2}</Box>
	</Box>
)

const INFO = 'The engine’s count for this strategy: offices × pickup dates × runs a day; sites and LORs travel inside each search.'

const searchesCell = (row, shopsLoading) => {
	const e = row.shop
	// "—" only when the engine has no number: still counting, or a
	// strategy it cannot compose (no product, no rows, invalid cadence).
	if (!e || e.searches_per_day == null) {
		const why = Object.entries(e?.skipped || {}).filter(([, n]) => n).map(([k]) => k.replace(/_/g, ' ')).join(', ')
		return <Box component="span" title={shopsLoading ? 'Counting with the engine…' : why ? `The engine cannot compose it: ${why}.` : 'The engine has no count for this strategy yet.'}>—</Box>
	}
	const shared = row.searchesShared || 0
	const sharedTitle = shared
		? ` ${fmtInt(shared)} of them are shared: office × pickup date × hour another strategy that is on also requests, so the engine launches them once for both.`
		: ''
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }} title={INFO + sharedTitle}>
			<Box component="span">
				<Box component="span" sx={{ fontWeight: 600 }}>{fmtInt(e.searches_per_day)}</Box>
				{shared > 0 && <Box component="span" sx={{ color: dsColorNew.successText, fontSize: 12 }}> ({fmtInt(shared)} shared)</Box>}
			</Box>
			<Box component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				{fmtInt(e.searches_per_run)} per run · {e.runs_per_day} run{e.runs_per_day === 1 ? '' : 's'}
			</Box>
		</Box>
	)
}

// Opens a strategy in the wizard on Basic, scrolled to card 3 "When does it run?".
function OpenStrategyLink({ id, children }) {
	const { onEdit } = useDsRepricingEdit(id)
	return (
		<Link component="button" type="button" onClick={() => onEdit(WHEN_IT_RUNS_STEP, WHEN_IT_RUNS_CARD)}
		      sx={{ fontWeight: 600, fontSize: 'inherit', verticalAlign: 'baseline', color: dsColorNew.appBlue, textDecorationColor: 'transparent',
		            '&:hover': { textDecorationColor: dsColorNew.appBlue } }}>
			{children}
		</Link>
	)
}

/**
 * Aligns strategy `b` to `a` (criterion 29): `b` is saved with `a`'s
 * frequency, window, days and hour list; its pickup / dropoff time and
 * everything else stay. One `PUT /strategies/<b>` with the whole strategy, then the
 * list and the engine's figures are reread. An older "all sites but Y" scope is
 * converted as the wizard does (`alignSiteScope`), never saved as "only Y";
 * `sitePatch` comes from the caller, which refused the align if it could not.
 */
async function alignStrategyHours(b, a, sitePatch = {}) {
	const after = alignHours(b.whenItRuns || factoryCadence(), a.whenItRuns || factoryCadence())
	const saved = fromServer(await strategiesApi.update(b.id, toServer(transformData({ ...b, ...sitePatch, whenItRuns: after }))))
	dsRepricingListData.value = dsRepricingListData.value.map(item => (item.id === saved.id ? saved : item))
	return saved
}

// A figure as the app's KPI cards (My Business, Activity): the icon in a tinted tile, the label in
// small caps, the figure, and the line that says what it counts.
function Figure({ icon, value, label, caption, tone = 'default' }) {
	const color = tone === 'good' ? dsColorNew.successText : tone === 'warn' ? dsColorNew.noteAmber : dsColorNew.appBlue
	return (
		<Box sx={{ flex: 1, minWidth: 220, p: 1.75, borderRadius: '12px', bgcolor: dsColorNew.white,
		           border: `1px solid ${dsColorNew.grayBorder}`, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
			<Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, display: 'inline-flex', alignItems: 'center',
			           justifyContent: 'center', bgcolor: tone === 'good' ? dsColorNew.successBg : tone === 'warn' ? dsColorNew.noteAmberBg : dsColorNew.pillActiveBg,
			           color }}>
				<IconSeeker icon={icon} sx={{ fontSize: 14 }}/>
			</Box>
			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }}>
					{label}
				</Typography>
				<Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: tone === 'default' ? dsColorNew.textTitle : color }}>
					{value}
				</Typography>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, lineHeight: 1.4, mt: 0.25 }}>
					{caption}
				</Typography>
			</Box>
		</Box>
	)
}

/** One advice row: an icon, the sentence, the action, the links. */
function AdviceRow({ icon, tone = 'warn', children, action }) {
	const fg = tone === 'good' ? dsColorNew.successText : dsColorNew.noteAmber
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.875, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>
			<IconSeeker icon={icon} sx={{ color: fg, fontSize: 14, flex: 'none' }}/>
			<Box sx={{ flex: 1, fontSize: 13, lineHeight: 1.45, color: dsColorNew.textDark }}>{children}</Box>
			{action}
		</Box>
	)
}

/**
 * "Rate shops per day" above the table — the engine's figures
 * (`GET /strategies/rate-shops`, criteria 27-29): what the tenant pays
 * (grouped — one search per office × pickup date × hour however many
 * strategies ask for it), what it would pay if every strategy ran alone,
 * and the difference; then the advice: pairs on the same offices and
 * dates at different hours, with the one-click fix (confirmed, UX v3 D21).
 * The warning threshold is edited here, where it is read.
 */
function RateShopsPanel({ data, shops }) {
	const { showToast } = useToast()
	const figures = shops.data
	const warning = figures?.warning ?? 0
	const grouped = figures?.grouped_total ?? null
	const plain = figures?.plain_total ?? null
	const hints = figures?.alignment_hints || []
	const saved = grouped != null && plain != null ? plain - grouped : null
	const over = warning > 0 && grouped != null && grouped > warning
	const on = data.filter(r => r.enabled).length
	const rowOf = (id) => data.find(r => String(r.id) === String(id)) || null
	const nameOf = (id) => rowOf(id)?.name || `#${id}`
	// Saved on blur through Setup (`PUT /setup/rate-shops-warning`), audited as tenant_config.
	const [threshold, setThreshold] = useState(warning)
	useEffect(() => { setThreshold(warning) }, [warning])
	const saveThreshold = async () => {
		const v = Math.max(1, Number(threshold) || warning)
		setThreshold(v)
		if (v === warning) return
		try { await shops.setWarning(v, 'Rate shops warning changed from the Strategies panel') }
		catch (e) { showToast.error(e?.message || 'Could not save the threshold'); setThreshold(warning) }
	}
	// Criterion 29 (UX v3 D21): "Align B to A" confirms with Now · After — the same dialog the wizard card uses.
	const [confirmAlign, alignDialog] = useConfirm()
	const [aligning, setAligning] = useState(null)
	const align = async (h) => {
		const a = rowOf(h.a), b = rowOf(h.b)
		if (!a || !b) return
		// B's product as the list decorated it (`decorate`): null when it is not in the catalogue.
		const bProduct = b.product?.product || null
		const sitesFit = alignSiteScope(b, bProduct ? productSites(bProduct) : null)
		if (sitesFit.error) { showToast.error(sitesFit.error); return }
		const before = b.whenItRuns || factoryCadence()
		const after = alignHours(before, a.whenItRuns || factoryCadence())
		if (!await confirmAlign(alignConfirmOptions({ aName: a.name, bName: b.name, before, after, saves: h.saves_per_day }))) return
		setAligning(`${h.a}|${h.b}`)
		try {
			await alignStrategyHours(b, a, sitesFit.patch)
			shops.reload({ quiet: true })
			const converted = sitesFit.patch.filterGroupSites?.length ? ' Its sites "all sites but …" were saved as the equivalent list.' : ''
			showToast.success(`"${b.name}" now runs when "${a.name}" runs.${converted}`)
		} catch (e) {
			showToast.error(e?.message || 'Could not align the strategies')
		} finally {
			setAligning(null)
		}
	}
	return (
		<Box sx={{ mb: 2, p: 2.5, borderRadius: '16px', position: 'relative',
		           bgcolor: dsColorNew.white,
		           border: `1px solid ${over ? dsColorNew.noteAmberBorder : dsColorNew.grayBorder}` }}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
				<AppCardTitle icon="magnifying-glass-dollar" title="Rate shops per day"
				              subtitle={`${on} strateg${on === 1 ? 'y' : 'ies'} on · the engine's count, shared searches counted once`}/>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
					<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, display: 'flex', alignItems: 'center', gap: 0.75 }}>
						Warn above
						<TextField size="small" type="number" value={threshold} onChange={e => setThreshold(e.target.value)} onBlur={saveThreshold}
						           disabled={!figures}
						           inputProps={{ min: 1, style: { width: 72, textAlign: 'right', fontWeight: 600, padding: '4px 8px' } }}/>
						searches / day
					</Typography>
					{shops.error && (
						<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber, fontWeight: 600 }} title={shops.error}>
							the engine did not answer ·{' '}
							<Link component="button" type="button" onClick={() => shops.reload()}
							      sx={{ fontSize: 'inherit', fontWeight: 600, verticalAlign: 'baseline', color: dsColorNew.appBlue }}>retry</Link>
						</Typography>
					)}
				</Box>
			</Box>

			<Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
				<Figure icon="rocket" label="Launched" value={grouped == null ? '—' : fmtInt(grouped)} tone={over ? 'warn' : 'default'}
				        caption="What the engine launches and what is billed: each office × pickup date × hour once, however many strategies ask for it."/>
				<Figure icon="layer-group" label="If every strategy ran alone" value={plain == null ? '—' : fmtInt(plain)}
				        caption="The sum of the strategies' own figures, as if nothing were shared."/>
				<Figure icon="piggy-bank" label="Saved by sharing" value={saved == null ? '—' : fmtInt(saved)} tone="good"
				        caption="Searches two or more strategies ask for at the same hour, launched once for all of them."/>
			</Box>

			{over && (
				<AppNote severity="warning" sx={{ mt: 1.5 }}>
					Launched is above the {fmtInt(warning)}/day threshold set here. Searches are billed.
				</AppNote>
			)}

			{hints.length > 0 && (
				<Box sx={{ mt: 1.75 }}>
					<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, mb: 0.5 }}>
						Advice
					</Typography>
					{hints.slice(0, 5).map((h, i) => (
						<AdviceRow key={i} icon="triangle-exclamation"
						           action={
						           	<ButtonLoadingSeeker size="small" variant="outlined"
						           	                     loading={aligning === `${h.a}|${h.b}`}
						           	                     onClick={() => align(h)}
						           	                     title={`Run "${nameOf(h.b)}" when "${nameOf(h.a)}" runs (${(h.a_hours || []).join(', ')}): frequency, window, days and hours. Asks first.`}
						           	                     sx={{ ...secondaryButtonSx, whiteSpace: 'nowrap', flex: 'none' }}>
						           		Align {nameOf(h.b)} to {nameOf(h.a)}
						           	</ButtonLoadingSeeker>
						           }>
							Strategies <OpenStrategyLink id={h.a}>{nameOf(h.a)}</OpenStrategyLink> and{' '}
							<OpenStrategyLink id={h.b}>{nameOf(h.b)}</OpenStrategyLink> run at <strong>{(h.a_hours || []).join(', ') || '—'}</strong> and <strong>{(h.b_hours || []).join(', ') || '—'}</strong> on the same offices and dates
							{h.shared_cells > 1 ? ` (${fmtInt(h.shared_cells)} office × date cells in common)` : ''}
							{' → '}Align them to share rate shops: <strong>saves ≈{fmtInt(h.saves_per_day)}/day</strong>.
						</AdviceRow>
					))}
				</Box>
			)}
			{alignDialog}
		</Box>
	)
}

// The Run now dialog's detail (CU-869fm5juc, mock `#run-ze` / `#run-fc`): the engine's searches and
// price points for the product, the strategies launched — each with its own price points, shared
// ones included — the engine's total, and the strategies of the product that are not launched. The
// front never sums: rows do not add up to the total, and the definition line says so.
const LIST_ROW_HEIGHT = 30

// A strategy opens in a NEW TAB (CU-869fm5ju2 UX Obs. 2): the dialog stays open.
const strategyHref = id => `${DS_REPRICING_ROUTES.CONFIGURATION_PATH}?rule=${encodeURIComponent(id)}`

function StrategyLine({ s, right, muted = false }) {
	return (
		<Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: LIST_ROW_HEIGHT }}>
			<Link href={strategyHref(s.id)} target="_blank" rel="noopener noreferrer" title={s.name}
			      sx={{ fontWeight: 600, color: muted ? dsColorNew.textMuted : dsColorNew.appBlue, minWidth: 0, flex: '0 1 auto',
			            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
			            textDecorationColor: 'transparent', '&:hover': { textDecorationColor: dsColorNew.appBlue } }}>
				{s.name}
			</Link>
			{s.priority != null && (
				<AppValueChip sx={{ py: 0.1, fontSize: 11, flex: 'none' }}>Prio {s.priority}</AppValueChip>
			)}
			<Box component="span" sx={{ ml: 'auto', flex: 'none', whiteSpace: 'nowrap' }}>{right}</Box>
		</Box>
	)
}

const groupSx = { mt: 1.25, pt: 1, borderTop: `1px solid ${dsColorNew.grayBorder}` }
const groupHeadSx = { fontSize: 12, fontWeight: 600, color: dsColorNew.textDark, mb: 0.5 }
const listSx = { listStyle: 'none', m: 0, p: 0, maxHeight: LIST_ROW_HEIGHT * 5, overflowY: 'auto' }

function RunNowDetail({ preview, off }) {
	const many = (preview?.launched.length || 0) > 1
	return (
		<Box>
			{preview ? (
				<Box>
					<strong>
						{searchesLabel(preview.total)}
						{preview.pricePoints != null && <> · {pricePointsLabel(preview.pricePoints)}</>}
					</strong>
					{preview.offices != null && preview.pickupDates != null && <> — {preview.offices} office{preview.offices === 1 ? '' : 's'} × {preview.pickupDates} pickup date{preview.pickupDates === 1 ? '' : 's'}</>}.
				</Box>
			) : NO_PREVIEW_DETAIL}
			{preview && preview.launched.length > 0 && (
				<Box sx={groupSx}>
					<Box sx={groupHeadSx}>Launched · {strategiesLabel(preview.launched.length)}</Box>
					<Box component="ul" sx={listSx}>
						{preview.launched.map(s => <StrategyLine key={s.id} s={s} right={s.pricePoints != null ? pricePointsLabel(s.pricePoints) : '—'}/>)}
					</Box>
					{many && (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5, pt: 0.5,
						           borderTop: `1px solid ${dsColorNew.grayBorder}`, fontWeight: 600, color: dsColorNew.textDark }}>
							<span>Total</span>
							<span>
								{searchesLabel(preview.total)}
								{preview.pricePoints != null && <> · {pricePointsLabel(preview.pricePoints)}</>}
							</span>
						</Box>
					)}
				</Box>
			)}
			{off.length > 0 && (
				<Box sx={groupSx}>
					<Box sx={groupHeadSx}>Not launched · {notLaunchedLabel(off)}</Box>
					<Box component="ul" sx={listSx}>
						{off.map(s => <StrategyLine key={s.id} s={s} muted right={<Box component="span" sx={{ color: dsColorNew.textMuted }}>{s.reason}</Box>}/>)}
					</Box>
				</Box>
			)}
			{preview && (
				<Box sx={{ mt: 1, fontSize: 12 }}>
					{PRICE_POINT_DEFINITION}
					{many && <> {PRICE_POINT_SHARED}</>} Searches are billed.
				</Box>
			)}
		</Box>
	)
}

// The product header as the mock draws it (`#list`, `.prow`): chevron, a 32 px tile, the name over
// its line, then Add strategy (a link) and Run now (a secondary button, blue icon).
const chevronSx = {
	width: 24, height: 24, minWidth: 0, p: 0, border: 0, borderRadius: '6px', bgcolor: 'transparent', cursor: 'pointer',
	display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', color: dsColorNew.textMuted,
	'&:hover': { color: dsColorNew.appBlue, bgcolor: dsColorNew.white },
	'&:disabled': { cursor: 'default', opacity: 0.4, color: dsColorNew.textMuted, bgcolor: 'transparent' },
}
const tileSx = {
	width: 32, height: 32, borderRadius: '8px', flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
	bgcolor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue, fontSize: 13,
}
const headerLinkSx = {
	display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 0.5, py: 0.25, borderRadius: '6px', border: 0, bgcolor: 'transparent',
	cursor: 'pointer', font: 'inherit', fontSize: 13, fontWeight: 500, color: dsColorNew.appBlue, textDecoration: 'none',
	'&:hover': { bgcolor: dsColorNew.hoverBgLight },
}
const headerButtonSx = {
	...secondaryButtonSx, height: 32, px: 1.5, py: 0, fontSize: 13, fontWeight: 500, minWidth: 0, whiteSpace: 'nowrap',
	'& .MuiButton-startIcon': { color: dsColorNew.appBlue, '& > *:nth-of-type(1)': { fontSize: 12 } }, '&.Mui-disabled': { opacity: 0.55, color: dsColorNew.textDark },
}

/**
 * The product's header row in the grouped table (CU-869fm5juc, mock
 * `#list`). Collapsed by default (Curro, 2026-10-26): the chevron opens
 * it, and the line under the name says what it holds — "N rows · N sites
 * · N strategies · N on". Add strategy opens the wizard with the product
 * chosen; Run now is disabled with its reason written next to it and in
 * its title (UX Obs. 4). The "No product" group has neither.
 */
function ProductHeader({ row, onRunNow, onAdd, onToggle }) {
	const p = row.product
	// A disabled button shows no tooltip of its own, so the reason sits on a wrapper (QA re-check).
	const chevron = (
		<Box component="span" title={row.locked ? LOCKED_OPEN : row.expanded ? 'Collapse' : 'Expand'} sx={{ display: 'inline-flex', flex: 'none' }}>
			<Box component="button" type="button" sx={chevronSx} aria-expanded={row.expanded} disabled={row.locked}
			     aria-label={row.locked ? LOCKED_OPEN : row.expanded ? 'Collapse' : 'Expand'}
			     onClick={e => { e.stopPropagation(); if (!row.locked) onToggle(row.key) }}>
				<IconSeeker icon={row.expanded ? 'chevron-down' : 'chevron-right'} sx={{ fontSize: 12 }}/>
			</Box>
		</Box>
	)
	if (!p) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
				{chevron}
				<Box sx={tileSx}><IconSeeker icon="triangle-exclamation" sx={{ fontSize: 13, color: dsColorNew.noteAmber }}/></Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
					<Box component="b" sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle, lineHeight: 1.3 }}>No product</Box>
					<Box component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{strategyCountLabel(row.strategies)} without a valid product</Box>
				</Box>
				<Chip size="small" label="no product — choose one" sx={toneChipSx('warn')}/>
			</Box>
		)
	}
	const state = runNowState(row.strategies)
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
			{chevron}
			<Box sx={tileSx}><IconSeeker icon="car" sx={{ fontSize: 13 }}/></Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
				<Box component="b" sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle, lineHeight: 1.3 }}>{p.name}</Box>
				<Box component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{productMeta(p, row.strategies)}</Box>
			</Box>
			<Box sx={{ flex: 1 }}/>
			{row.strategies.length > 0 && (
				<Box component="button" type="button" sx={headerLinkSx} onClick={e => { e.stopPropagation(); onAdd(p.id) }}>
					<IconSeeker icon="plus" sx={{ fontSize: 12 }}/>Add strategy
				</Box>
			)}
			{state.disabled && (
				<Box component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted, whiteSpace: 'nowrap' }}>{state.reason}</Box>
			)}
			<span title={state.disabled ? state.reason : `Launch a pass of every strategy on in ${p.name}, now`}>
				<ButtonLoadingSeeker size="small" variant="outlined"
				                     disabled={state.disabled}
				                     loading={!!row.running}
				                     startIcon={<IconSeeker icon="play" sx={{ fontSize: 12 }}/>}
				                     onClick={e => { e.stopPropagation(); onRunNow(p, row.strategies) }}
				                     sx={headerButtonSx}>
					Run now
				</ButtonLoadingSeeker>
			</span>
		</Box>
	)
}

/** The strategy's name opens it, as in the mock (`td.name a`): blue, semibold; an off one reads muted. */
function NameCell({ row }) {
	const { onEdit } = useDsRepricingEdit(row.id)
	return (
		<Link component="button" type="button" onClick={e => { e.stopPropagation(); onEdit() }}
		      sx={{ fontWeight: 600, fontSize: 'inherit', color: row.enabled ? dsColorNew.appBlue : dsColorNew.textMuted,
		            textDecorationColor: 'transparent', '&:hover': { textDecorationColor: 'currentColor' } }}>
			{row.name}
		</Link>
	)
}

/** A product without strategies, opened (criterion 2, mock `#list` `tr.empty`): what it means, and its first strategy. */
function EmptyProduct({ row, onAdd }) {
	const name = row.product?.name
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
			<Box component="span" sx={{ color: dsColorNew.textMuted }}>
				No strategy prices <Box component="b" sx={{ color: dsColorNew.textDark }}>{name}</Box> yet: the engine leaves its pricelists as they are in the PMS.
			</Box>
			<ButtonLoadingSeeker size="small" variant="outlined" onClick={e => { e.stopPropagation(); onAdd(row.product.id) }}
			                     startIcon={<IconSeeker icon="plus" sx={{ fontSize: 12 }}/>} sx={headerButtonSx}>
				Add a strategy to {name}
			</ButtonLoadingSeeker>
		</Box>
	)
}

/** What a new strategy starts as — from the toolbar, or from a product's header with the product chosen. */
const newStrategy = (productId = null) => ({ name: null, priority: DEFAULT_PRIORITY, productId, compareAgainst: { ...DEFAULT_COMPARE }, whenItRuns: factoryCadence(), strategy: { type: 'market_trend', rankCap: null, excludeOutliersPct: null }, undercut: { value: 2.5, type: 1, direction: 0 }, competitorRule: null, tethers: [], advanced: null, period: emptyPeriod(), tools: null, filterGroupOfficesExclusion: false, filterGroupSitesExclusion: false, filterGroupWebSuppliersExclusion: false, filterGroupProvidersExclusion: false, filterGroupWebSuppliers: [], filterAcrissExclusion: false, filterCategoriesExclusion: false, compareByCategory: false })

// The Off/On toggle's word for a row, as the old checkbox column filtered it: Draft / Enabled / Disabled.
const statusWord = row => (row?.status === 'draft' ? 'Draft' : (row?.enabled ? 'Enabled' : 'Disabled'))

// Dropdown filters whose values were read off the table before CU-869fm5juc: with product rows in the
// table, every dropdown names its values explicitly (TL C1 d), from the strategies only.
const valueFilterOptions = (data, field) => ({
	names: [...new Set(data.map(row => row?.[field]).filter(Boolean))],
	logic: (value, filters) => {
		if (!filters?.length) return false
		return !filters.includes(value)
	},
})

// What the search box looks in: the strategy's own words and its product's name (UX Obs. 3).
const searchText = row => [row.name, row.productName, ...(row.sitesLabels || []), strategyLabel(row.strategy),
	...periodLabels(row.period), row.cadenceLine1, row.cadenceLine2].filter(Boolean).join(' ')

// The value a header's sort reads, per column (sorted inside each product, Prio breaking ties).
const SORT_VALUE = {
	name: r => r.name,
	priority: r => r.priority ?? DEFAULT_PRIORITY,
	strategy: r => strategyLabel(r.strategy),
	durations: r => (Array.isArray(r.durations) && r.durations.length ? Number(r.durations[0].durationFrom) : null),
	searchesPerDay: r => r.searchesPerDay,
	tethers: r => (Array.isArray(r.tethers) ? r.tethers.length : 0),
	enabled: r => (r.enabled ? 1 : 0),
	id: r => r.name,
}

/** The catalogues the wizard and the table read — the axis names, the
 *  site names, the PMS pricelists, the products and the engine's rate
 *  shops — loaded once per page, not per open (CU-869fe6mf4, CU-869fhuhy6). */
export default function DsRepricingConfigurationPage() {
	return (
		<AxisCatalogProvider>
			<SiteCatalogProvider>
				<PricelistCatalogProvider>
					<ProductsProvider>
						<RateShopsProvider>
							<StrategiesPage/>
						</RateShopsProvider>
					</ProductsProvider>
				</PricelistCatalogProvider>
			</SiteCatalogProvider>
		</AxisCatalogProvider>
	)
}

function StrategiesPage() {
	const { getTableData } = useDsRepricingTable()
	const [overlapState, setOverlapState] = useState(null)
	const { onSubmit } = useDsRepricingFormSubmit({
		getTableData,
		// `onConfirm` is null for a blocking (same-priority) overlap: there is nothing to confirm
		// past, only the other rule to go fix. Otherwise it saves anyway, priority already decided.
		onOverlap: (conflicts, onConfirm) => setOverlapState({ conflicts, onConfirm }),
	})
	const { onDelete } = useDsRepricingActions()
	const [confirmEdit, setConfirmEdit] = useState(null)
	const sites = useSiteCatalog()
	const shops = useTenantRateShops()
	const products = useProductsCatalog()
	const list = dsRepricingListData.value
	const data = useMemo(() => decorate(list, shops.data, products.items, sites), [list, shops.data, products.items, sites])

	// A save changes what the engine would launch: reread its figures.
	useEffect(() => { if (!dsRepricingIsLoading.value) shops.reload({ quiet: true }) }, [list])

	// Run now from a product's header (CU-869fm5juc): the engine's count for the product as the
	// preview, then `POST /strategies/products/<id>/run-now`. Executions marks the pass by hand.
	const [confirm, confirmDialog] = useConfirm()
	const { showToast } = useToast()
	const [running, setRunning] = useState(null)
	const onRunNow = async (product, strategies) => {
		const preview = productRunNowPreview(shops.data, product.id)
		// Only the engine's own "0 searches" stops here (TL C2); a product missing from a cached
		// estimate opens the dialog without figures, and the server has the last word.
		if (launchesNothing(preview)) {
			showToast.info(`"${product.name}" would launch nothing right now — the engine finds nothing to search for its strategies on.`)
			return
		}
		const ok = await confirm({
			title: titleOf(product.name),
			subtitle: SUBTITLE,
			body: BODY,
			detail: <RunNowDetail preview={preview} off={notLaunched(strategies, preview)}/>,
			confirmLabel: confirmLabel(preview),
		})
		if (!ok) return
		setRunning(product.id)
		try {
			const r = await strategiesApi.runProductNow(product.id, 'Run now from the Strategies table')
			const outcome = launchOutcome(product.name, r)
			if (outcome.ok) showToast.success(outcome.text)
			else showToast.info(outcome.text)
		} catch (e) {
			showToast.error(e?.message || 'Could not launch the pass')
		} finally {
			setRunning(null)
		}
	}
	// TableSeeker memoises its columns on their JSON (functions drop out), so a cell's closure can be
	// older than this render: the cells call the latest handlers through refs.
	const runNowRef = useRef(onRunNow)
	runNowRef.current = onRunNow
	const addStrategy = (productId) => { dsRepricingDrawerData.value = newStrategy(productId) }

	// The table runs server-side (TL C1, plan D10): it neither searches, filters, sorts nor pages;
	// `groupByProduct` does, by product, from what the table reports here.
	const [view, setView] = useState({ search: '', filterList: [], sortOrder: null, page: 0, perPage: 20 })
	// Every product starts collapsed (Curro, 2026-10-26): the keys of the ones the user opened. Kept
	// here, not in the table, so a reload of the list (after a save) does not close them again.
	const [expanded, setExpanded] = useState(() => new Set())
	const onToggle = key => setExpanded(e => toggleExpanded(e, key))  // locked headers never call it (QA re-check)
	const onTableChange = (action, state) => {
		if (!['search', 'filterChange', 'resetFilters', 'sort'].includes(action)) return
		setView(v => ({ ...v, search: state.searchText || '', filterList: state.filterList || [],
		                sortOrder: state.sortOrder?.name ? state.sortOrder : null, page: 0 }))
	}

	// `?rule=<id>` opens that rule straight away: it is the link the overlap warning uses to send
	// the user to the conflicting rule in a new tab.
	const [searchParams] = useSearchParams()
	const requestedRuleId = searchParams.get('rule')
	const { onEdit: openRequestedRule } = useDsRepricingEdit(requestedRuleId)

	const handleSubmit = (data) => {
		if (data.id) {
			setConfirmEdit({ data, original: dsRepricingOriginalData.peek() })
		} else {
			onSubmit(data)
		}
	}

	// "Save anyway" only reaches here for a non-blocking overlap (priority already breaks the tie),
	// so the modal is dismissed and the save that was held back runs.
	const handleOverlapConfirm = () => {
		overlapState?.onConfirm?.()
		setOverlapState(null)
	}

	useEffect(() => {
		getTableData()
	}, [])

	useEffect(() => {
		if (requestedRuleId) openRequestedRule()
	}, [requestedRuleId])

	return <PageWrapper
		head={`DS Rentacar | ${DS_REPRICING_ROUTES.STRATEGIES_TITLE}`}
		title={DS_REPRICING_ROUTES.STRATEGIES_TITLE}
		subtitle={DS_REPRICING_ROUTES.STRATEGIES_SUBTITLE}
	>
		{/* The one-time migration banner (UX D11): the rate cards became
		    Products and the scheduler went into each strategy. */}
		<MigrationBanner where="strategies"/>
		{confirmDialog}
		<DsRepricingDrawer onSubmit={handleSubmit} isLoading={dsRepricingIsSubmitting.value}/>
		{confirmEdit && (
			<ConfirmEditModal
				open
				original={confirmEdit.original}
				current={confirmEdit.data}
				isLoading={dsRepricingIsSubmitting.value}
				onClose={() => setConfirmEdit(null)}
				onConfirm={() => { onSubmit(confirmEdit.data); setConfirmEdit(null) }}
			/>
		)}
		{overlapState && (
			<OverlapModal
				open
				conflicts={overlapState.conflicts}
				onConfirm={overlapState.onConfirm ? handleOverlapConfirm : null}
				onClose={() => setOverlapState(null)}
			/>
		)}
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{dsRepricingIsLoading.value
					? <AppResultsSkeleton/>
					: <>
					<RateShopsPanel data={data} shops={shops}/>
					<StrategiesTable data={data} products={products.items} view={view} setView={setView} expanded={expanded} onToggle={onToggle}
					                 onTableChange={onTableChange} running={running} shopsLoading={shops.loading}
					                 onDelete={onDelete}
					                 onRunNow={(p, list) => runNowRef.current(p, list)} onAdd={addStrategy}/>
					</>
				}
			</Grid>
		</Grid>
	</PageWrapper>
}

// One header + its strategies per product; every other column is today's (mock `#list`).
const VISIBLE_COLUMNS = 11
const isStrategy = row => row?.kind === 'strategy'
// The cells as the mock paints them (`.tbl tr.prod td`, `tr.empty td`, `.tbl td`), with the app's tokens:
// a product header is a soft blue band with white air above it; its strategies are white rows striped
// inside the group (not by table row, so a header never shifts the zebra); an off strategy reads muted.
const PRODUCT_DIVIDER = alpha(dsColorNew.appBlue, 0.2)
const productCellSx = (first) => ({
	bgcolor: dsColorNew.pillActiveBg, textAlign: 'left', px: 1.5, py: 1.25,
	borderTop: first ? 0 : `12px solid ${dsColorNew.white}`, borderBottom: `1px solid ${PRODUCT_DIVIDER}`,
})
const emptyCellSx = { bgcolor: dsColorNew.white, textAlign: 'left', pl: 5.5, pr: 1.5, py: 1.75, color: dsColorNew.textMuted, borderBottom: `1px solid ${dsColorNew.grayBorder}` }
const strategyCellSx = (row) => ({
	// the mock stripes `tr:nth-child(even)`: the header is row 1, so a group's first strategy is the tinted one
	bgcolor: row.stripe % 2 === 0 ? dsColorNew.tableRowAlt : dsColorNew.white,
	color: row.enabled ? dsColorNew.textDark : dsColorNew.textMuted,
	borderBottom: `1px solid ${dsColorNew.grayBorder}`, py: 1.25,
})
// Product and empty rows are one cell across the table: the first column spans, the rest hide.
const spanFirst = rows => (_, dataIndex) => {
	const row = rows[dataIndex]
	// the name sits left, indented 40 px under the header's chevron as in the mock
	if (isStrategy(row)) return { sx: { ...strategyCellSx(row), textAlign: 'left', pl: '40px' } }
	return { colSpan: VISIBLE_COLUMNS, sx: row?.kind === 'product' ? productCellSx(dataIndex === 0) : emptyCellSx }
}
const hideOnGroup = rows => (_, dataIndex) => (isStrategy(rows[dataIndex]) ? { sx: strategyCellSx(rows[dataIndex]) } : { style: { display: 'none' } })
// A strategy cell renderer that draws nothing on a product or empty row.
const onStrategy = render => (v, row, ...rest) => (isStrategy(row) ? render(v, row, ...rest) : null)

/**
 * The Strategies table grouped by product (CU-869fm5juc, plan D10 after
 * the F0 spike — TL C1): `AppDataTable` straight, not `AppCrudTable`,
 * whose row style ignores the caller's; neither it nor `@ui-seeker/core`
 * is touched. mui-datatables runs `serverSide`, so it keeps the search
 * box, the filter dialog with its chips, the sort arrows and the footer,
 * and `groupByProduct` does the searching, filtering, sorting and the
 * paging by product. Every dropdown names its values (no product row in
 * them); the footer says "N products · M strategies".
 */
export function StrategiesTable({ data, products, view, setView, expanded, onToggle, onTableChange, running, shopsLoading, onDelete, onRunNow, onAdd }) {
	const columns = [
		{ name: 'name', label: 'Name', options: { filterType: 'textField', filterOptions: { logic: (v, f) => !String(v ?? '').toLowerCase().includes(String(f[0] ?? '').toLowerCase()) },
			customBodyRowRender: (v, row) => (row.kind === 'product' ? <ProductHeader row={row} onRunNow={onRunNow} onAdd={onAdd} onToggle={onToggle}/>
				: row.kind === 'empty' ? <EmptyProduct row={row} onAdd={onAdd}/> : <NameCell row={row}/>) } },
		{ name: 'priority', label: 'Prio', options: { filter: false, customBodyRowRender: onStrategy(v => centered(v ?? DEFAULT_PRIORITY)) } },
		/* Since CU-869fm5juc the product is the group, not a column; its filter stays, excluded like Undercut. */
		{ name: 'productName', label: 'Product', options: { display: 'excluded', filterType: 'dropdown', filterOptions: valueFilterOptions(data, 'productName') } },
		{ name: 'sitesLabels', label: 'Sites', options: { sort: false, filterType: 'dropdown', filterOptions: sitesFilterOptions(data), customBodyRowRender: onStrategy((_, row) => centered(sitesCell(row))) } },
		{ name: 'strategy', label: 'Strategy', options: { filterType: 'dropdown', filterOptions: strategyFilterOptions(dsRepricingListData.value), customBodyRowRender: onStrategy((v, row) => centered(strategyCell(v, row.undercut))) } },
		{ name: 'period', label: 'Period', options: { sort: false, filterType: 'dropdown', filterOptions: periodFilterOptions(dsRepricingListData.value), customBodyRowRender: onStrategy(v => { const labels = periodLabels(v); return centered(labels.length > 0 ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{labels.map((label, i) => <span key={i}>{label}</span>)}</Box> : '-') }) } },
		{ name: 'durations', label: 'Durations', options: { filterType: 'dropdown', filterOptions: rangeFilterOptions(dsRepricingListData.value, 'durations', 'durationFrom', 'durationTo'), customBodyRowRender: onStrategy(v => centered(Array.isArray(v) && v.length > 0 ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{v.map((d, i) => <span key={i}>{`${d.durationFrom} → ${d.durationTo}`}</span>)}</Box> : '-')) } },
		{ name: 'cadenceLine1', label: 'When it runs', options: { sort: false, filterType: 'dropdown', filterOptions: valueFilterOptions(data, 'cadenceLine1'), customBodyRowRender: onStrategy((_, row) => centered(cadenceCell(row))) } },
		{ name: 'searchesPerDay', label: 'Searches / day', options: { filter: false, customBodyRowRender: onStrategy((_, row) => centered(searchesCell(row, shopsLoading))) } },
		// Undercut stays filterable without a column: folded into Strategy (UX D6), the
		// same `display: 'excluded'` trick Off/On uses below.
		{ name: 'undercut', label: 'Undercut', options: { display: 'excluded', filterType: 'dropdown', filterOptions: undercutFilterOptions(dsRepricingListData.value) } },
		{ name: 'tethers', label: 'Tethers', options: { filter: false, customBodyRowRender: onStrategy(v => centered(tethersSummary(v))) } },
		/* Removed columns (moved to Setup):
		     • Max Mark-Up (%)   → Setup → Products (markup cap per site row)
		     • Rate cards        → Products (CU-869fhuhy6, brief v3); the product is the group (CU-869fm5juc)
		     • Tethering summary → Setup → Tethering (the strategy
		       still surfaces the count of applied ones as `Tethers`)
		     • Min / Max (€/day) → Setup → Price Limits           */
		{
			name: 'enabled', label: 'Off/On',
			options: {
				display: 'excluded',
				filterType: 'checkbox',
				filterList: dsRepricingStatusFilter.value,
				filterOptions: { names: ['Enabled', 'Disabled', 'Draft'] },
			}
		},
		{ name: 'enabled', label: 'Off/On', options: { filter: false, customBodyRowRender: onStrategy((v, row) => <StatusSwitch value={v} row={row}/>) } },
		{ name: 'id', label: 'Actions', options: { filter: false, sort: false, customBodyRowRender: onStrategy((_, row) => <ActionsCell row={row} onDelete={onDelete}/>) } },
	]
	const STATUS_COLUMN = 11

	// The column filters as `groupByProduct` reads them: each column's own `logic` on the row's value.
	// The Off/On toggle is read from its signal and hides strategies only (`narrows: false`).
	const filters = [
		...columns.map((c, i) => (i === STATUS_COLUMN || !c.options?.filterOptions?.logic ? null : {
			value: row => row[c.name], logic: c.options.filterOptions.logic, values: view.filterList[i] || [],
		})).filter(Boolean),
		{ value: statusWord, logic: (v, f) => !f.includes(v), values: dsRepricingStatusFilter.value || [], narrows: false },
	]
	const sortValue = view.sortOrder ? SORT_VALUE[view.sortOrder.name] : null
	const grouped = groupByProduct(data, products, {
		search: view.search, searchOf: searchText, filters,
		sort: sortValue ? { value: sortValue, direction: view.sortOrder.direction } : null,
		page: view.page, perPage: view.perPage, expanded,
	})
	const rows = flatten(grouped.groups).map(r => (r.kind === 'product' ? { ...r, running: r.product != null && running === r.product.id } : r))
	const columnsWithSpan = columns.map((c, i) => ({
		...c, options: { ...c.options, setCellProps: i === 0 ? spanFirst(rows) : hideOnGroup(rows) },
	}))

	return (
		<AppDataTable
			title={<AppCardTitle icon="arrow-trend-up" title={DS_REPRICING_ROUTES.STRATEGIES_TITLE} subtitle="By product · lowest Prio decides">
				<Box component="span" sx={{ ml: 0.75 }}>
					<AppStatusFilterToggle aria-label="Strategies shown" filter={dsRepricingStatusFilter.value || []}
					                       onChange={filter => { dsRepricingStatusFilter.value = filter; setView(v => ({ ...v, page: 0 })) }}/>
				</Box>
			</AppCardTitle>}
			toolbarProps={{ actions: false }}
			data={rows}
			columns={columnsWithSpan}
			options={{
				search: true,
				searchPlaceholder: 'Search strategy or product',
				serverSide: true,
				count: grouped.groupCount,
				onTableChange,
				customSearch: undefined,
				// The cells carry the colours (`setCellProps`); the row only adds the hover of a strategy row.
				// Set here so AppDataTable's own zebra, which counts table rows, does not apply.
				enhancedSetRowProps: (row) => ({
					sx: isStrategy(row)
						? { backgroundColor: 'transparent', '&:hover > td': { backgroundColor: `${dsColorNew.hoverBgLight} !important` } }
						: { backgroundColor: 'transparent', '&.MuiTableRow-hover:hover': { backgroundColor: 'transparent' } },
				}),
				customFooter: () => (
					<TableFooter>
						<TableRow>
							<TablePagination count={grouped.groupCount} page={grouped.page} rowsPerPage={view.perPage}
							                 rowsPerPageOptions={[5, 10, 20, 50]}
							                 labelRowsPerPage="Products per page:"
							                 labelDisplayedRows={() => footerLabel(grouped.productCount, grouped.strategyCount)}
							                 onPageChange={(_, page) => setView(v => ({ ...v, page }))}
							                 onRowsPerPageChange={e => setView(v => ({ ...v, perPage: Number(e.target.value) || 20, page: 0 }))}/>
						</TableRow>
					</TableFooter>
				),
				customToolbar: () => <AppToolbarCreateButton
					label="New strategy"
					icon="file-plus"
					sx={{ textTransform: 'none' }}
					onClick={() => onAdd(null)}
				/>,
				// the Off/On filter has its own selector next to the title, so it gets no chip
				setFilterChipProps: (_, columnName) => columnName === 'enabled' ? { className: HIDDEN_FILTER_CHIP } : {},
				onFilterChange: (changedColumn, filterList, _, dataIdx) => {
					if (changedColumn === 'enabled')
						dsRepricingStatusFilter.value = filterList[dataIdx]
				},
				// The Excel export pointed at the legacy `/ds/list/excel`, which
				// reads `DS_REPRICING` by `uid`. After the move to the canonical
				// tables it would hand the operator a spreadsheet that does not
				// match the list in front of them, so it is off rather than wrong
				// (brief CU-869f043c1, Duda 3 — reinstating it is a PO call).
				download: false,
			}}
		/>
	)
}
